import { effJiraKey } from '../../utils/jira';
import { getActivePhase } from '../../utils/phases';

// email → dev, pour pré-remplir les worklogs non résolus dans le modal
export function emailToDevIdMap(adminUsers) {
  const map = {};
  for (const u of adminUsers) if (u.email) map[u.email.toLowerCase()] = u.id;
  return map;
}

// clé projet Jira (MAJ, ex. "VGP") → client + brique par défaut de sa phase, pour pré-remplir
// le "Projet" des worklogs non matchés : le projet se déduit toujours du préfixe du ticket
// (ex. "VGP-548"), même quand l'epic est introuvable — inutile de le laisser vide.
export function projectKeyToClientMap(clients) {
  const map = {};
  for (const c of clients || []) {
    for (const ph of c.phases || []) {
      const pk = effJiraKey(ph, c).toUpperCase();
      if (!pk || map[pk]) continue;
      const def = (ph.briques || []).find((b) => b.isJiraDefault);
      map[pk] = { clientId: c.id, phaseId: ph.id, briqueId: def?.id || '' };
    }
  }
  return map;
}

// Worklog non rattaché, avec le membre et le projet devinés.
export function withRemap(u, emailToDevId, projectKeyToClient) {
  const issuePrefix = (u.issueKey || '').match(/^([A-Z]+)/)?.[1] || '';
  const auto = projectKeyToClient[issuePrefix.toUpperCase()];
  return {
    ...u,
    month: (u.date || '').slice(0, 7),
    remapDevId: emailToDevId[(u.authorEmail || '').toLowerCase()] || '',
    remapClientId: auto?.clientId || '',
    remapPhaseId: auto?.phaseId || '',
    remapBriqueId: auto?.briqueId || '',
  };
}

export const epicKeys = (clients) =>
  (clients || []).flatMap((c) =>
    (c.phases || []).flatMap((ph) => (ph.briques || []).map((b) => b.jira_epic).filter(Boolean)),
  );

// Lignes de consommation Jira persistées (epics, projets, projets par mois).
export function consumptionRows({ byEpic, byProject, byProjectMonth, byProjectDay = {} }, now) {
  const row = (kind, jira_key, heures) => ({ kind, jira_key, heures, synced_at: now });
  return [
    ...Object.entries(byEpic).map(([k, h]) => row('epic', k, h)),
    ...Object.entries(byProject).map(([k, h]) => row('project', k, h)),
    ...Object.entries(byProjectMonth).flatMap(([projKey, months]) =>
      Object.entries(months).map(([month, h]) => row('project_month', `${projKey}:${month}`, h)),
    ),
    // Totaux par jour : le consommé Jira d'une phase se calcule au jour près sur ses dates.
    ...Object.entries(byProjectDay).flatMap(([projKey, days]) =>
      Object.entries(days).map(([day, h]) => row('project_day', `${projKey}:${day}`, h)),
    ),
  ];
}

/* Routage utilisé par la synchro côté serveur.
   epic_map : un même epic peut être une brique dans plusieurs phases (ex. Cognlab : Maintenance +
   Phase 1 + Phase 2). On route vers la brique de la PHASE ACTIVE (rang 3), sinon la phase avec une
   date de début (rang 2, la plus récente gagne), sinon n'importe laquelle (rang 1). Évite que le
   worklog parte sur la mauvaise phase.
   default_brique_map : clé projet Jira (MAJ) → brique "Général" (is_jira_default) de la phase.
   Permet au cron de rattacher un worklog sans epic à la brique par défaut du bon projet (cas
   Lahaye : BEMOD/LAH/LM). */
export function jiraSyncConfigMaps(clients) {
  const epic_map = {};
  const epicRank = {};
  const default_brique_map = {};
  for (const c of clients || []) {
    const activePhase = getActivePhase(c);
    for (const ph of c.phases || []) {
      const rank = ph.id === activePhase?.id ? 3 : ph.dateDebut ? 2 : 1;
      for (const b of ph.briques || []) {
        if (!b.jira_epic) continue;
        const prev = epicRank[b.jira_epic];
        const better =
          !prev ||
          rank > prev.rank ||
          (rank === prev.rank && (ph.dateDebut || '') > (prev.dateDebut || ''));
        if (!better) continue;
        epic_map[b.jira_epic] = { brique_id: b.id, brique_nom: b.nom, client_nom: c.nom };
        epicRank[b.jira_epic] = { rank, dateDebut: ph.dateDebut };
      }
      const phKey = effJiraKey(ph, c);
      const def = phKey && (ph.briques || []).find((b) => b.isJiraDefault);
      if (def)
        default_brique_map[phKey.toUpperCase()] = {
          brique_id: def.id,
          brique_nom: def.nom,
          client_nom: c.nom,
        };
    }
  }
  return { epic_map, default_brique_map };
}
